<template>
  <div class="search-bar">
    <div class="search-inner">
      <div class="search-field">
        <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="7"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          v-model="keyword"
          type="text"
          placeholder="공고 전체 검색 (제목, 현장, 회사, 지역, 상세내용 등)"
          class="search-input"
          @keyup.enter="handleSearch"
        />
        <button v-if="keyword" class="clear-btn" @click="keyword = ''">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="6" y1="6" x2="18" y2="18"/>
            <line x1="6" y1="18" x2="18" y2="6"/>
          </svg>
        </button>
      </div>

      <div class="filters">
        <select v-model="filters.region" @change="handleSearch">
          <option value="">지역</option>
          <option v-for="r in regions" :key="r" :value="r">{{ r }}</option>
        </select>
        <select v-model="filters.industry" @change="handleSearch">
          <option value="">업종</option>
          <option v-for="i in industries" :key="i" :value="i">{{ i }}</option>
        </select>
        <select v-model="filters.jobType" @change="handleSearch">
          <option value="">직종</option>
          <option v-for="j in jobTypes" :key="j" :value="j">{{ j }}</option>
        </select>
        <select v-model="filters.salaryType" @change="handleSearch">
          <option value="">급여</option>
          <option v-for="s in salaryTypes" :key="s" :value="s">{{ s }}</option>
        </select>
      </div>

      <button class="search-btn" @click="handleSearch">검색</button>
    </div>

    <!-- 모바일 -->
    <div class="mobile-search">
      <div class="mobile-search-field">
        <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="7"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          v-model="keyword"
          type="text"
          placeholder="전체 검색"
          class="search-input"
          @keyup.enter="handleSearch"
        />
      </div>
      <button class="filter-btn" :class="{ active: activeCount > 0 }" @click="showFilters = !showFilters">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="4" y1="6" x2="20" y2="6"/>
          <line x1="8" y1="12" x2="16" y2="12"/>
          <line x1="11" y1="18" x2="13" y2="18"/>
        </svg>
        <span v-if="activeCount" class="filter-count">{{ activeCount }}</span>
      </button>
    </div>

    <transition name="expand">
      <div v-if="showFilters" class="mobile-filters">
        <div class="filter-group">
          <label>지역</label>
          <select v-model="filters.region">
            <option value="">전체</option>
            <option v-for="r in regions" :key="r" :value="r">{{ r }}</option>
          </select>
        </div>
        <div class="filter-group">
          <label>업종</label>
          <select v-model="filters.industry">
            <option value="">전체</option>
            <option v-for="i in industries" :key="i" :value="i">{{ i }}</option>
          </select>
        </div>
        <div class="filter-group">
          <label>직종</label>
          <select v-model="filters.jobType">
            <option value="">전체</option>
            <option v-for="j in jobTypes" :key="j" :value="j">{{ j }}</option>
          </select>
        </div>
        <div class="filter-group">
          <label>급여</label>
          <select v-model="filters.salaryType">
            <option value="">전체</option>
            <option v-for="s in salaryTypes" :key="s" :value="s">{{ s }}</option>
          </select>
        </div>
        <div class="filter-actions">
          <button class="reset-btn" @click="resetFilters">초기화</button>
          <button class="apply-btn" @click="applyFilters">적용</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

export default {
  name: 'SearchHeader',
  emits: ['search'],
  setup(props, { emit }) {
    const route = useRoute()
    const router = useRouter()
    
    const keyword = ref('')
    const showFilters = ref(false)
    
    const filters = reactive({
      region: '',
      industry: '',
      jobType: '',
      salaryType: ''
    })

    const regions = ['서울', '경기', '인천', '부산', '대구', '광주', '대전', '울산', '세종', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주']
    const industries = ['아파트', '오피스텔', '도시형생활주택', '호텔', '레지던스', '상가', '쇼핑몰', '오피스', '지식산업센터', '토지', '빌라', '타운하우스', '펜션', '전원주택', '콘도', '리조트', '기타']
    const jobTypes = ['시행/업무대행', '건설사', '분양대행', '본부장', '본부', '팀장', '팀원', '정규직', '데스크', 'TM상담사', '홍보관도우미', '거점알바', '호객알바', '전단알바', '직투알바', '현수막', '기타']
    const salaryTypes = ['계약수수료', '기본급', '기본급+인센티브', '시급', '일급', '주급', '월급']

    const activeCount = computed(() => Object.values(filters).filter(v => v).length)

    // URL 쿼리에서 필터 로드
    const loadFromQuery = () => {
      keyword.value = route.query.keyword || ''
      filters.region = route.query.region || ''
      filters.industry = route.query.industry || ''
      filters.jobType = route.query.jobType || ''
      filters.salaryType = route.query.salaryType || ''
    }

    const handleSearch = () => {
      const searchData = { 
        keyword: keyword.value, 
        ...filters 
      }
      
      // emit으로 부모에게 알림 (SearchView에서 사용)
      emit('search', searchData)
      
      // 검색 페이지로 이동 (다른 페이지에서 사용)
      if (route.path !== '/search') {
        router.push({
          path: '/search',
          query: {
            ...(keyword.value && { keyword: keyword.value }),
            ...(filters.region && { region: filters.region }),
            ...(filters.industry && { industry: filters.industry }),
            ...(filters.jobType && { jobType: filters.jobType }),
            ...(filters.salaryType && { salaryType: filters.salaryType })
          }
        })
      }
    }

    const resetFilters = () => {
      filters.region = ''
      filters.industry = ''
      filters.jobType = ''
      filters.salaryType = ''
    }

    const applyFilters = () => {
      showFilters.value = false
      handleSearch()
    }

    // 현재 페이지가 검색 페이지면 URL 쿼리 로드
    watch(() => route.query, () => {
      if (route.path === '/search') {
        loadFromQuery()
      }
    }, { immediate: true })

    onMounted(() => {
      if (route.path === '/search') {
        loadFromQuery()
      }
    })

    return {
      keyword,
      filters,
      showFilters,
      regions,
      industries,
      jobTypes,
      salaryTypes,
      activeCount,
      handleSearch,
      resetFilters,
      applyFilters
    }
  }
}
</script>

<style scoped>
.search-bar {
  background: #fff;
  border-bottom: 1px solid #f2f4f6;
}

.search-inner {
  max-width: 1120px;
  margin: 0 auto;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-field {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 16px;
  color: #8b95a1;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 14px 44px;
  background: #f9fafb;
  border: 1px solid transparent;
  border-radius: 12px;
  font-size: 15px;
  font-family: inherit;
  color: #191f28;
  transition: all 0.15s;
}

.search-input::placeholder {
  color: #8b95a1;
}

.search-input:focus {
  outline: none;
  background: #fff;
  border-color: #e5e8eb;
  box-shadow: 0 0 0 3px rgba(49, 130, 246, 0.08);
}

.clear-btn {
  position: absolute;
  right: 12px;
  padding: 6px;
  background: none;
  border: none;
  color: #8b95a1;
  cursor: pointer;
  display: flex;
  transition: color 0.15s;
}

.clear-btn:hover {
  color: #4e5968;
}

.filters {
  display: flex;
  gap: 8px;
}

.filters select {
  padding: 14px 36px 14px 16px;
  background: #f9fafb;
  border: 1px solid transparent;
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  color: #191f28;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238b95a1' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  transition: all 0.15s;
}

.filters select:focus {
  outline: none;
  background-color: #fff;
  border-color: #e5e8eb;
}

.search-btn {
  padding: 14px 24px;
  background: #3182f6;
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;
}

.search-btn:hover {
  background: #1b64da;
}

.mobile-search {
  display: none;
  padding: 12px 16px;
  gap: 8px;
}

.mobile-search-field {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.mobile-search-field .search-icon {
  left: 14px;
}

.mobile-search-field .search-input {
  padding: 12px 16px 12px 40px;
  font-size: 14px;
  border-radius: 10px;
}

.filter-btn {
  position: relative;
  padding: 12px;
  background: #f9fafb;
  border: none;
  border-radius: 10px;
  color: #4e5968;
  cursor: pointer;
  display: flex;
  transition: all 0.15s;
}

.filter-btn.active {
  background: #e8f3ff;
  color: #3182f6;
}

.filter-count {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  background: #3182f6;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-filters {
  padding: 16px;
  background: #f9fafb;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-group label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7684;
}

.filter-group select {
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  color: #191f28;
}

.filter-actions {
  grid-column: 1 / -1;
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.reset-btn {
  flex: 1;
  padding: 14px;
  background: #fff;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  color: #4e5968;
  cursor: pointer;
}

.apply-btn {
  flex: 2;
  padding: 14px;
  background: #3182f6;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  color: #fff;
  cursor: pointer;
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 768px) {
  .search-inner {
    display: none;
  }

  .mobile-search {
    display: flex;
  }
}
</style>